export const pageIds = [
  'overview',
  'models',
  'model-detail',
  'playground',
  'deploy',
  'endpoint',
  'evaluations',
  'requests',
  'usage',
] as const;

export type PageId = (typeof pageIds)[number];
export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';
export type ModelAccess = 'Serverless' | 'Dedicated' | 'Both';
export type ModelModality = 'Text' | 'Vision' | 'Image' | 'Embedding' | 'Audio';

export interface PageMeta {
  readonly id: PageId;
  readonly label: string;
  readonly shortLabel: string;
  readonly group: 'Start' | 'Discover' | 'Build' | 'Evaluate' | 'Deploy' | 'Operate';
  readonly description: string;
}

export interface ModelRecord {
  readonly id: string;
  readonly name: string;
  readonly provider: string;
  readonly summary: string;
  readonly modalities: readonly ModelModality[];
  readonly capabilities: readonly string[];
  readonly context: number;
  readonly inputPrice: number;
  readonly outputPrice: number;
  readonly latencyP50: number;
  readonly throughput: number;
  readonly availability: number;
  readonly access: ModelAccess;
  readonly regions: readonly string[];
  readonly featured?: boolean;
}

export interface ProviderRoute {
  readonly id: string;
  readonly provider: string;
  readonly region: string;
  readonly inputPrice: number;
  readonly outputPrice: number;
  readonly latencyP50: number;
  readonly throughput: number;
  readonly availability: number;
  readonly privacy: 'Standard' | 'Zero retention';
}

export interface RequestRecord {
  readonly id: string;
  readonly time: string;
  readonly model: string;
  readonly route: string;
  readonly key: string;
  readonly status: 200 | 400 | 429 | 500;
  readonly latency: number;
  readonly ttft: number;
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly cost: number;
  readonly traceId: string;
}

export interface TraceSpan {
  readonly id: string;
  readonly parentId: string | null;
  readonly label: string;
  readonly kind: 'gateway' | 'policy' | 'model' | 'tool';
  readonly start: number;
  readonly duration: number;
  readonly tone: Tone;
  readonly detail: string;
}

export const pages: readonly PageMeta[] = [
  {
    id: 'overview',
    label: 'Project overview',
    shortLabel: 'Overview',
    group: 'Start',
    description: 'Activation, service health, recent resources and budget risk.',
  },
  {
    id: 'models',
    label: 'Model catalog',
    shortLabel: 'Models',
    group: 'Discover',
    description: 'Search, filter and compare model capabilities, price and performance.',
  },
  {
    id: 'model-detail',
    label: 'Aurora 3.2 Reason',
    shortLabel: 'Model detail',
    group: 'Discover',
    description: 'A decision page connecting providers, evaluation, playground and deployment.',
  },
  {
    id: 'playground',
    label: 'Playground',
    shortLabel: 'Playground',
    group: 'Build',
    description: 'Run multimodal prompts and inspect code, events and usage.',
  },
  {
    id: 'evaluations',
    label: 'Evaluations',
    shortLabel: 'Evaluations',
    group: 'Evaluate',
    description: 'Compare models and investigate quality regressions at sample level.',
  },
  {
    id: 'deploy',
    label: 'Create dedicated endpoint',
    shortLabel: 'Create endpoint',
    group: 'Deploy',
    description: 'Configure model, region, capacity, security and cost.',
  },
  {
    id: 'endpoint',
    label: 'support-copilot-prod',
    shortLabel: 'Endpoints',
    group: 'Deploy',
    description: 'Operate versions, traffic, metrics, logs and endpoint settings.',
  },
  {
    id: 'requests',
    label: 'Requests and traces',
    shortLabel: 'Requests',
    group: 'Operate',
    description: 'Attribute failures and latency to requests, spans, keys and routes.',
  },
  {
    id: 'usage',
    label: 'Usage and access',
    shortLabel: 'Usage & keys',
    group: 'Operate',
    description: 'Understand spend, budgets and project-scoped API credentials.',
  },
] as const;

export const models: readonly ModelRecord[] = [
  {
    id: 'aurora-3-2-reason',
    name: 'Aurora 3.2 Reason',
    provider: 'Northstar Research',
    summary: 'High-accuracy reasoning for agents, analysis and structured workflows.',
    modalities: ['Text', 'Vision'],
    capabilities: ['Tool calling', 'Structured output', 'Reasoning'],
    context: 200000,
    inputPrice: 2.4,
    outputPrice: 9.6,
    latencyP50: 610,
    throughput: 142,
    availability: 99.98,
    access: 'Both',
    regions: ['cn-shanghai', 'ap-southeast-1', 'us-west-2'],
    featured: true,
  },
  {
    id: 'atlas-2-7-chat',
    name: 'Atlas 2.7 Chat',
    provider: 'Meridian AI',
    summary: 'Balanced instruction model for customer support and knowledge assistants.',
    modalities: ['Text', 'Vision'],
    capabilities: ['Tool calling', 'JSON mode', 'Caching'],
    context: 128000,
    inputPrice: 0.9,
    outputPrice: 3.6,
    latencyP50: 320,
    throughput: 238,
    availability: 99.97,
    access: 'Both',
    regions: ['cn-shanghai', 'ap-southeast-1'],
    featured: true,
  },
  {
    id: 'prism-1-4-vision',
    name: 'Prism 1.4 Vision',
    provider: 'Luma Systems',
    summary: 'Visual understanding for documents, screenshots and product media.',
    modalities: ['Text', 'Vision'],
    capabilities: ['OCR', 'Grounding', 'Structured output'],
    context: 64000,
    inputPrice: 1.2,
    outputPrice: 4.8,
    latencyP50: 480,
    throughput: 176,
    availability: 99.94,
    access: 'Serverless',
    regions: ['cn-shanghai', 'ap-southeast-1'],
  },
  {
    id: 'canvas-2-image',
    name: 'Canvas 2 Image',
    provider: 'Luma Systems',
    summary: 'Controllable image generation and editing for product workflows.',
    modalities: ['Image'],
    capabilities: ['Text to image', 'Image edit', 'Reference image'],
    context: 4096,
    inputPrice: 0.03,
    outputPrice: 0.08,
    latencyP50: 2400,
    throughput: 4,
    availability: 99.91,
    access: 'Serverless',
    regions: ['ap-southeast-1', 'us-west-2'],
    featured: true,
  },
  {
    id: 'vector-3-embed',
    name: 'Vector 3 Embed',
    provider: 'Harbor Compute',
    summary: 'Multilingual embeddings optimized for retrieval and semantic clustering.',
    modalities: ['Embedding'],
    capabilities: ['Multilingual', 'Matryoshka', 'Batch'],
    context: 32000,
    inputPrice: 0.04,
    outputPrice: 0,
    latencyP50: 38,
    throughput: 4800,
    availability: 99.99,
    access: 'Both',
    regions: ['cn-shanghai', 'ap-southeast-1', 'us-west-2'],
  },
  {
    id: 'echo-2-speech',
    name: 'Echo 2 Speech',
    provider: 'Sonic Labs',
    summary: 'Streaming speech recognition for meetings and real-time assistants.',
    modalities: ['Audio'],
    capabilities: ['Streaming', 'Diarization', 'Timestamps'],
    context: 0,
    inputPrice: 0.006,
    outputPrice: 0,
    latencyP50: 180,
    throughput: 55,
    availability: 99.93,
    access: 'Dedicated',
    regions: ['cn-shanghai'],
  },
] as const;

export const providerRoutes: readonly ProviderRoute[] = [
  {
    id: 'northstar-cn',
    provider: 'Northstar direct',
    region: 'cn-shanghai',
    inputPrice: 2.4,
    outputPrice: 9.6,
    latencyP50: 610,
    throughput: 142,
    availability: 99.98,
    privacy: 'Zero retention',
  },
  {
    id: 'harbor-sg',
    provider: 'Harbor routed',
    region: 'ap-southeast-1',
    inputPrice: 2.18,
    outputPrice: 9.2,
    latencyP50: 740,
    throughput: 168,
    availability: 99.95,
    privacy: 'Standard',
  },
  {
    id: 'meridian-us',
    provider: 'Meridian capacity',
    region: 'us-west-2',
    inputPrice: 2.55,
    outputPrice: 9.4,
    latencyP50: 520,
    throughput: 126,
    availability: 99.99,
    privacy: 'Zero retention',
  },
] as const;

export const requestRecords: readonly RequestRecord[] = [
  {
    id: 'req_7f2a91',
    time: '12:42:18.504',
    model: 'Aurora 3.2 Reason',
    route: 'support-copilot-prod',
    key: 'prod-support',
    status: 200,
    latency: 1834,
    ttft: 412,
    inputTokens: 1842,
    outputTokens: 486,
    cost: 0.0091,
    traceId: 'tr_91c83a',
  },
  {
    id: 'req_7f2a73',
    time: '12:41:56.201',
    model: 'Atlas 2.7 Chat',
    route: 'serverless-auto',
    key: 'prod-support',
    status: 200,
    latency: 792,
    ttft: 188,
    inputTokens: 936,
    outputTokens: 302,
    cost: 0.0019,
    traceId: 'tr_91c807',
  },
  {
    id: 'req_7f2a55',
    time: '12:41:21.923',
    model: 'Aurora 3.2 Reason',
    route: 'support-copilot-prod',
    key: 'prod-support',
    status: 429,
    latency: 84,
    ttft: 0,
    inputTokens: 0,
    outputTokens: 0,
    cost: 0,
    traceId: 'tr_91c7e9',
  },
  {
    id: 'req_7f2a40',
    time: '12:40:44.118',
    model: 'Prism 1.4 Vision',
    route: 'serverless-cn',
    key: 'eval-runner',
    status: 200,
    latency: 2140,
    ttft: 690,
    inputTokens: 2480,
    outputTokens: 620,
    cost: 0.0059,
    traceId: 'tr_91c7d1',
  },
  {
    id: 'req_7f2a22',
    time: '12:39:52.776',
    model: 'Atlas 2.7 Chat',
    route: 'serverless-auto',
    key: 'staging-web',
    status: 500,
    latency: 4603,
    ttft: 0,
    inputTokens: 1118,
    outputTokens: 0,
    cost: 0.001,
    traceId: 'tr_91c7b8',
  },
] as const;

export const traceSpans: readonly TraceSpan[] = [
  {
    id: 'span-gateway',
    parentId: null,
    label: 'POST /v1/responses',
    kind: 'gateway',
    start: 0,
    duration: 1834,
    tone: 'success',
    detail: 'Authenticated project and normalized the Responses request.',
  },
  {
    id: 'span-policy',
    parentId: 'span-gateway',
    label: 'Guardrail · support-production',
    kind: 'policy',
    start: 22,
    duration: 38,
    tone: 'neutral',
    detail: 'Prompt injection and sensitive-data checks passed.',
  },
  {
    id: 'span-retrieve',
    parentId: 'span-gateway',
    label: 'Tool · search_knowledge',
    kind: 'tool',
    start: 74,
    duration: 286,
    tone: 'info',
    detail: 'Returned 6 support policy passages from the current index.',
  },
  {
    id: 'span-model',
    parentId: 'span-gateway',
    label: 'Aurora 3.2 Reason',
    kind: 'model',
    start: 382,
    duration: 1420,
    tone: 'success',
    detail: 'Northstar direct · 1,842 input · 486 output tokens.',
  },
] as const;

export const endpoint = {
  id: 'ep_7c3f19',
  name: 'support-copilot-prod',
  status: 'Healthy',
  model: 'Aurora 3.2 Reason',
  version: '2026-08-12',
  environment: 'Production',
  region: 'cn-shanghai',
  url: 'https://api.aster.example/v1/endpoints/support-copilot-prod',
  replicas: 3,
  minReplicas: 2,
  maxReplicas: 8,
  traffic: [
    { revision: 'rev-18', share: 90, status: 'Stable' },
    { revision: 'rev-19', share: 10, status: 'Canary' },
  ],
  metrics: {
    requests24h: 184200,
    p50: 684,
    p95: 1840,
    ttftP50: 318,
    errorRate: 0.27,
    tokensPerSecond: 138,
  },
} as const;

export const evaluation = {
  id: 'eval_42c9',
  name: 'Support policy regression · v18',
  status: 'Completed',
  dataset: 'support-policy-golden-v6',
  samples: 240,
  completedAt: '2026-08-24 18:42 CST',
  candidates: [
    { model: 'Aurora 3.2 Reason', quality: 91.8, grounded: 94.2, safety: 99.4, p95: 1880 },
    { model: 'Atlas 2.7 Chat', quality: 87.6, grounded: 89.1, safety: 99.7, p95: 1080 },
    { model: 'Prism 1.4 Vision', quality: 82.3, grounded: 86.8, safety: 98.9, p95: 2240 },
  ],
  badCases: [
    {
      id: 'sample-018',
      category: 'Policy date',
      input: '退款窗口从签收还是下单日期开始计算？',
      expected: '从确认签收之日开始计算，普通商品为 7 天。',
      output: '退款窗口从下单日期开始计算。',
      score: 0,
      traceId: 'tr_91c83a',
    },
    {
      id: 'sample-073',
      category: 'Citation',
      input: '企业客户是否支持分批开票？',
      expected: '支持，需要按照结算单批次申请。',
      output: '支持分批开票，但没有给出结算单批次限制。',
      score: 0.5,
      traceId: 'tr_91c807',
    },
    {
      id: 'sample-144',
      category: 'Escalation',
      input: '同一订单第三次申请价保应该怎么处理？',
      expected: '转人工审批，不承诺自动退款。',
      output: '已为你自动发起价保退款。',
      score: 0,
      traceId: 'tr_91c7e9',
    },
  ],
} as const;

export const usage = {
  currency: 'USD',
  currentSpend: 1842.36,
  budget: 2500,
  forecast: 2368,
  credits: 820.42,
  byModel: [
    { name: 'Aurora 3.2 Reason', cost: 1048.2, share: 56.9, tokens: 184_200_000 },
    { name: 'Atlas 2.7 Chat', cost: 438.82, share: 23.8, tokens: 318_400_000 },
    { name: 'Prism 1.4 Vision', cost: 211.49, share: 11.5, tokens: 48_600_000 },
    { name: 'Other models', cost: 143.85, share: 7.8, tokens: 92_300_000 },
  ],
  byDay: [612, 748, 701, 882, 940, 1104, 982, 1218, 1342, 1280, 1516, 1602, 1842],
  apiKeys: [
    { name: 'prod-support', prefix: 'ak_live_••••7Q2M', lastUsed: '2 min ago', limit: 1800 },
    { name: 'eval-runner', prefix: 'ak_live_••••K9PA', lastUsed: '18 min ago', limit: 400 },
    { name: 'staging-web', prefix: 'ak_test_••••2TX8', lastUsed: '3 h ago', limit: 120 },
  ],
} as const;

export const projectContext = {
  organization: 'Meridian Labs',
  project: 'Support Copilot',
  environment: 'Production',
  region: 'cn-shanghai',
} as const;

export function isPageId(value: string | null): value is PageId {
  return value !== null && (pageIds as readonly string[]).includes(value);
}

export function readPage(search: string): PageId {
  const candidate = new URLSearchParams(search).get('view');
  return isPageId(candidate) ? candidate : 'overview';
}

export function viewHref(view: PageId): string {
  return `?view=${view}`;
}

export function formatTokens(value: number): string {
  return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  );
}

export function formatMoney(value: number, maximumFractionDigits = 2): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits,
  }).format(value);
}
